import React, { FC, PropsWithChildren } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { brand, outlinePillButton, radius } from './brand';

export type PhaseTone = 'light' | 'white' | 'muted';

const tones: Record<PhaseTone, {background: string, divider: string}> = {
    light: {background: brand.background, divider: brand.border},
    white: {background: brand.surface, divider: brand.border},
    muted: {background: brand.backgroundAlt, divider: brand.accentSoft},
};

interface PhaseSectionProps {
    tone: PhaseTone;
    eyebrow: string;
    title: string;
    intro: string;
    step?: number;
    id?: string;
    compact?: boolean;
    nextHref?: string;
    nextLabel?: string;
}

export const PhaseSection: FC<PhaseSectionProps & PropsWithChildren> = (
        {tone, eyebrow, title, intro, step, id, compact = false, nextHref, nextLabel, children}) => {
    const {background, divider} = tones[tone];

    return (
        <Box
            id={id}
            component="section"
            sx={{
                background,
                borderTop: `1px solid ${divider}`,
                scrollMarginTop: {xs: '16px', md: '28px'},
                paddingTop: compact ? {xs: '40px', md: '56px'} : {xs: '56px', md: '84px'},
                paddingBottom: compact ? {xs: '40px', md: '56px'} : {xs: '56px', md: '84px'},
            }}>
            <Container maxWidth="lg">
                <Stack spacing={compact ? 3 : 4}>
                    <Stack
                        direction={{xs: 'column', md: 'row'}}
                        spacing={{xs: 2, md: 4}}
                        alignItems={{md: 'flex-start'}}>

                        {step !== undefined && (
                            <Box
                                aria-hidden
                                sx={{
                                    flexShrink: 0,
                                    width: {xs: 48, md: 64},
                                    height: {xs: 48, md: 64},
                                    borderRadius: '50%',
                                    background: brand.navy,
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: {xs: '1.4rem', md: '1.85rem'},
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                    lineHeight: 1,
                                }}>
                                {step}
                            </Box>
                        )}

                        <Stack spacing={1.25}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <Box sx={{width: 26, height: 2, background: brand.green, borderRadius: '2px'}}/>
                                <Typography sx={{
                                    color: brand.blue,
                                    fontWeight: 700,
                                    fontSize: '.78rem',
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                }}>
                                    {eyebrow}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="h2"
                                sx={{
                                    color: brand.navy,
                                    fontWeight: 700,
                                    letterSpacing: '-.02em',
                                    fontSize: compact
                                        ? {xs: '1.4rem', md: '1.7rem'}
                                        : {xs: '1.85rem', md: '2.4rem'},
                                }}>
                                {title}
                            </Typography>
                            <Typography sx={{
                                color: brand.text,
                                maxWidth: '62ch',
                                fontSize: compact ? '.98rem' : '1.05rem',
                                lineHeight: 1.65,
                            }}>
                                {intro}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Box sx={{paddingLeft: {md: step !== undefined ? '96px' : 0}}}>
                        <Stack spacing={4}>
                            {children}

                            {nextHref && (
                                <Button
                                    href={nextHref}
                                    variant="outlined"
                                    data-testid={`next-step-${id ?? 'section'}`}
                                    endIcon={<ArrowDownwardIcon sx={{fontSize: 18, color: brand.green}}/>}
                                    sx={{...outlinePillButton, alignSelf: 'flex-start'}}>
                                    {nextLabel}
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

// The three phases in one line, directly under the hero. A visitor sees the shape
// of the whole thing — and can jump into any of it — before scrolling.
export const PhaseOverview: FC<{items: {step: number, label: string, href: string}[]}> = ({items}) => (
    <Container maxWidth="lg" sx={{paddingTop: {xs: '20px', md: '28px'}, paddingBottom: {xs: '20px', md: '32px'}}}>
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={{xs: 1.25, sm: 0}}
            alignItems={{sm: 'center'}}
            sx={{
                background: brand.surface,
                border: `1px solid ${brand.border}`,
                borderRadius: radius.pill,
                padding: {xs: '18px 22px', sm: '14px 28px'},
            }}>
            {items.map((item, index) => (
                <React.Fragment key={item.step}>
                    <Stack
                        component="a"
                        href={item.href}
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{
                            textDecoration: 'none',
                            borderRadius: radius.pill,
                            padding: '6px 12px',
                            margin: '-6px -12px',
                            transition: 'background .18s ease',
                            '&:hover': {background: brand.backgroundAlt},
                            '&:focus-visible': {outline: `2px solid ${brand.blue}`, outlineOffset: '2px'},
                            '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
                        }}>
                        <Box sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: brand.accentLight,
                            color: brand.navy,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '.78rem',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                            {item.step}
                        </Box>
                        <Typography sx={{color: brand.navy, fontWeight: 600, fontSize: '.94rem'}}>
                            {item.label}
                        </Typography>
                    </Stack>
                    {index < items.length - 1 && (
                        <Box
                            aria-hidden
                            sx={{
                                display: {xs: 'none', sm: 'block'},
                                flex: 1,
                                height: '1px',
                                background: brand.border,
                                margin: '0 18px',
                            }}/>
                    )}
                </React.Fragment>
            ))}
        </Stack>
    </Container>
);
